import React from 'react';
import {RouterProvider, createBrowserRouter, defer} from 'react-router-dom';
import {createRoot} from 'react-dom/client';
import {App} from './app';
import {SelectLoginMethod} from './pages/login/select_login_method';
import {Dashboard} from './pages/dashboard/dashboard';
import {LoginWithEmail} from './pages/login/login_with_email';
import './scss/main.scss';
import {Provider} from 'react-redux';
import {store} from './store/store';
import {tokenLoader} from './loaders/token_loader';
import {LoginWithFacebook} from './pages/login/login_with_facebook';
import {ProtectedRoute} from './components/protected_route';
import {redirectIfAuthorized, redirectIfUnauthorized} from './support/redirect_if_unauthorized';
import {signOutLoader} from './loaders/signout_loader';
import {meLoader} from './loaders/me_loader';
import {Chat} from './pages/chat/chat';
import {Contact} from './pages/contact/contact';
import {chatLoader, unreadChatLoader} from './loaders/chat_loader';
import {Occasions} from './pages/occasions/occasions';
import {notificationsLoader} from './loaders/notifications_loader';
import {appConfigLoader} from './loaders/app_config_loader';
import {Profile} from './pages/profile/profile';
import {Notifications} from './pages/notifications/notifications';
import {Notification} from './pages/notifications/notification';
import {postMessageAction} from './actions/post_message_action';
import {Faults} from './pages/faults/faults';
import {News} from './pages/news/news';
import {Reviews} from './pages/reviews/reviews';
import {Information} from './pages/information/information';
import {Damage} from './pages/damage/damage';
import {Appointment} from './pages/appointment/appointment';
import {occasionBrandsLoader} from './loaders/occasion_brands_loader';
import {Onboarding} from './pages/onboarding/onboarding';
import {postProfileAction} from './actions/post_profile_action';
import {OccasionDetails} from './pages/occasion_details/occasion_details';
import {Navigation} from './components/navigation/navigation';
import {Vehicles} from './pages/vehicles/vehicles.tsx';
import {privacyAgreementAction} from './actions/privacy_agreement_action.ts';
import {VehicleDetails} from './pages/vehicle_details/vehicle_details.tsx';
import {NotificationEmpty} from './pages/notifications/notification_empty.tsx';
import {NewsDetails} from './pages/news_details/news_details.tsx';
import {LoginWithGoogle} from './pages/login/login_with_google.tsx';
import {OccasionOfferForm} from './pages/occasion_details/modals/offer_form.tsx';
import {postOfferForm} from './actions/occasions/post_offer_form.ts';
import {LeadSuccessForm} from './pages/occasion_details/modals/lead_success.tsx';
import {OccasionTradeinForm} from './pages/occasion_details/modals/tradein_form.tsx';
import {postTradeinForm} from './actions/occasions/post_tradein_form.ts';
import {OccasionTestDriveForm} from './pages/occasion_details/modals/test_drive_form.tsx';
import {postTestDriveForm} from './actions/occasions/post_test_drive_form.ts';
import {postVehicleAction} from './actions/post_vehicle_action.ts';
import {postAppointmentAction} from './actions/post_appointment_action.ts';
import {AppointmentSuccess} from './pages/appointment/appointment_success.tsx';
import {ErrorPage} from './pages/error.tsx';

(window as unknown as {__store__: typeof store}).__store__ = store;

const router = createBrowserRouter([
    {
        loader: async () => {
            const appConfig = await appConfigLoader(store);
            return defer({
                appConfig,
                unreadChat: unreadChatLoader(store),
                notifications: notificationsLoader(store),
                user: meLoader(store),
            });
        },
        errorElement: <ErrorPage />,
        element: <App />,
        children: [
            {
                loader: () => redirectIfUnauthorized(store) ?? meLoader(store),
                path: '/onboarding',
                element: <Onboarding />,
                action: async ({request}) => await privacyAgreementAction(store, request),
            },
            {
                element: <Navigation />,
                children: [
                    {
                        path: '/login',
                        loader: async ({request}) =>
                            (await tokenLoader(store, request.url)) ?? redirectIfAuthorized(store),
                        children: [
                            {
                                index: true,
                                element: <SelectLoginMethod />,
                            },
                            {
                                path: '/login/email',
                                element: <LoginWithEmail />,
                                loader: () => redirectIfAuthorized(store),
                            },
                            {
                                path: '/login/facebook',
                                element: <LoginWithFacebook />,
                                loader: () => redirectIfAuthorized(store),
                            },
                            {
                                path: '/login/google',
                                element: <LoginWithGoogle />,
                                loader: () => redirectIfAuthorized(store),
                            },
                        ],
                    },
                    {
                        path: '/damage',
                        element: <Damage />,
                    },
                    {
                        path: '/news',
                        element: <News />,
                    },
                    {
                        path: '/news/:newsId',
                        element: <NewsDetails />,
                    },
                    {
                        path: '/reviews',
                        element: <Reviews />,
                    },
                    {
                        path: '/info',
                        element: <Information />,
                    },
                    {
                        path: '/contact',
                        element: <Contact />,
                    },
                    {
                        path: '/occasions',
                        element: <Occasions />,
                        loader: () => occasionBrandsLoader(store),
                    },
                    {
                        // Must be not /occasions/:occasionId due to scroll restoration
                        path: '/occasion/:occasionId',
                        element: <OccasionDetails />,
                    },
                    {
                        path: '/occasion/:occasionId/offer',
                        element: <OccasionOfferForm />,
                        loader: () => meLoader(store),
                        action: async ({request}) => postOfferForm(store, request),
                    },
                    {
                        path: '/occasion/:occasionId/tradein',
                        element: <OccasionTradeinForm />,
                        loader: () => meLoader(store),
                        action: async ({request}) => postTradeinForm(store, request),
                    },
                    {
                        path: '/occasion/:occasionId/testdrive',
                        element: <OccasionTestDriveForm />,
                        loader: () => meLoader(store),
                        action: async ({request}) => postTestDriveForm(store, request),
                    },
                    {
                        loader: () => meLoader(store),
                        path: '/occasion/:occasionId/bedankt/:leadType',
                        element: <LeadSuccessForm />,
                    },
                    {
                        loader: () => redirectIfUnauthorized(store) ?? meLoader(store),
                        children: [
                            {
                                index: true,
                                element: <Dashboard />,
                            },
                            {
                                path: '/profile',
                                element: <Profile />,
                                action: async ({request}) => postProfileAction(store, request),
                            },
                            {
                                path: '/vehicles',
                                element: <Vehicles />,
                            },
                            {
                                path: '/vehicles/:vehicleId',
                                element: <VehicleDetails />,
                                action: async ({request}) => postVehicleAction(store, request),
                            },
                            {
                                path: '/chat',
                                element: <Chat />,
                                loader: () => chatLoader(store),
                                action: async ({request}) => postMessageAction(store, request),
                            },
                            {
                                path: '/notifications',
                                element: <Notifications />,
                                children: [
                                    {
                                        index: true,
                                        element: <NotificationEmpty />,
                                    },
                                    {
                                        path: '/notifications/:notificationId',
                                        element: <Notification />,
                                    },
                                ],
                            },
                            {
                                path: '/appointment',
                                element: <Appointment />,
                                action: async ({request}) => postAppointmentAction(store, request),
                            },
                            {
                                path: '/appointment/success',
                                element: <AppointmentSuccess />,
                            },
                            {
                                path: '/faults',
                                element: <Faults />,
                            },
                            {
                                path: `/dashboard`,
                                element: (
                                    <ProtectedRoute>
                                        <Dashboard />
                                    </ProtectedRoute>
                                ),
                            },
                            {
                                path: '/logout',
                                loader: () => signOutLoader(store),
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: '*',
        element: <ErrorPage code={404} />,
    },
]);

createRoot(document.getElementById('app')!).render(
    <React.StrictMode>
        <Provider store={store}>
            <RouterProvider router={router} />
        </Provider>
    </React.StrictMode>,
);
